import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { SubscriptionSummary } from '../services/accountService';
import WtiLoader from './WtiLoader';

interface SubscriptionUsageCardProps {
  summary: SubscriptionSummary | null;
  loading?: boolean;
}

function formatLimit(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return 'Unlimited';
  if (value === 'Unlimited') return 'Unlimited';
  return String(value);
}

const SubscriptionUsageCard: React.FC<SubscriptionUsageCardProps> = ({ summary, loading }) => {
  if (loading) {
    return (
      <section className="dashboard-subscription-card card" aria-label="Subscription">
        <WtiLoader size="sm" message="Loading subscription…" />
      </section>
    );
  }

  if (!summary) {
    return null;
  }

  if (!summary.hasSubscription && !summary.exemptFromLimits) {
    return (
      <section className="dashboard-subscription-card card dashboard-subscription-card--inactive" aria-label="Subscription">
        <p>{summary.message || 'No active subscription found.'}</p>
      </section>
    );
  }

  if (summary.exemptFromLimits) {
    return (
      <section className="dashboard-subscription-card card" aria-label="Subscription">
        <div className="dashboard-subscription-card__header">
          <span className="dashboard-subscription-card__icon" aria-hidden="true">
            <FontAwesomeIcon icon={faCreditCard} />
          </span>
          <div>
            <h2>Admin access</h2>
            <p>Usage limits are not applied to your account.</p>
          </div>
        </div>
      </section>
    );
  }

  const phase = summary.access?.phase;
  const statusLabel = summary.cancelAtPeriodEnd
    ? 'CANCELS AT PERIOD END'
    : phase === 'grace'
      ? 'GRACE'
      : phase === 'soft_locked'
        ? 'PAYMENT DUE'
        : summary.status;
  const statusClass =
    summary.cancelAtPeriodEnd
      ? 'dashboard-subscription-card__status--cancelling'
      : phase === 'active' || summary.status === 'ACTIVE'
        ? 'dashboard-subscription-card__status--active'
        : 'dashboard-subscription-card__status--inactive';

  return (
    <section className="dashboard-subscription-card card" aria-label="Subscription">
      <div className="dashboard-subscription-card__header">
        <span className="dashboard-subscription-card__icon" aria-hidden="true">
          <FontAwesomeIcon icon={faCreditCard} />
        </span>
        <div>
          <h2>Your plan</h2>
          <p className="dashboard-subscription-card__plan">{summary.displayName}</p>
        </div>
        <span className={`dashboard-subscription-card__status ${statusClass}`}>
          {statusLabel}
        </span>
      </div>

      <div className="dashboard-subscription-card__grid">
        <div className="dashboard-subscription-card__stat">
          <p className="dashboard-subscription-card__label">Reports used</p>
          <p className="dashboard-subscription-card__value">
            {summary.limits?.reportsUsed ?? 0}
            {' / '}
            {formatLimit(summary.limits?.reportsPerPeriodLabel)}
          </p>
          {summary.limits?.reportsRemaining !== null && summary.limits?.reportsRemaining !== undefined && (
            <p className="dashboard-subscription-card__hint">
              {summary.limits.reportsRemaining} remaining this period
            </p>
          )}
        </div>

        <div className="dashboard-subscription-card__stat">
          <p className="dashboard-subscription-card__label">Saved reports</p>
          <p className="dashboard-subscription-card__value">
            {summary.usage?.savedReportsUsed ?? 0}
            {' / '}
            {formatLimit(summary.limits?.savedReportsLimitLabel)}
          </p>
        </div>

        <div className="dashboard-subscription-card__stat">
          <p className="dashboard-subscription-card__label">History access</p>
          <p className="dashboard-subscription-card__value">
            {summary.limits?.historyAccessLabel || '—'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default SubscriptionUsageCard;
